import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects } from '../context/ProjectContext';
import { useCRM } from '../context/CRMContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Modal } from '../components/ui/Modal';
import { formatCurrency } from '../utils/calculations';
import { PlusIcon, ChevronDownIcon, ChevronRightIcon, TrashIcon, DollarSignIcon, UserIcon, XIcon, ArrowLeftIcon, StickyNoteIcon, CalendarIcon, ClockIcon } from 'lucide-react';
export function ProjectFlow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    selectedCompany
  } = useData();
  const {
    getProjectsByCompanyId,
    toggleProjectComplete,
    toggleTaskComplete,
    deleteProject,
    deleteTask,
    addProject,
    addTask,
    updateProject,
    addNote,
    deleteNote
  } = useProjects();
  const {
    getContactsByCompanyId
  } = useCRM();
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectValue, setNewProjectValue] = useState('');
  const [newProjectContactId, setNewProjectContactId] = useState('');
  const [newTaskTitles, setNewTaskTitles] = useState<Record<string, string>>({});
  const [newTaskValues, setNewTaskValues] = useState<Record<string, string>>({});
  const [contactFilter, setContactFilter] = useState('');
  // Note modal state
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [noteDateTime, setNoteDateTime] = useState('');
  const [includeDateTime, setIncludeDateTime] = useState(false);
  const projects = selectedCompany ? getProjectsByCompanyId(selectedCompany.id) : [];
  const contacts = selectedCompany ? getContactsByCompanyId(selectedCompany.id) : [];
  // Check for contact filter in URL params
  useEffect(() => {
    const contactId = searchParams.get('contact');
    if (contactId) {
      setContactFilter(contactId);
    }
  }, [searchParams]);
  // Filter projects by contact if filter is active
  const filteredProjects = contactFilter ? projects.filter(p => p.contact_id === contactFilter) : projects;
  const clearContactFilter = () => {
    setContactFilter('');
    setSearchParams({});
  };
  const goBackToContact = () => {
    if (contactFilter) {
      navigate(`/lead-flow/view/${contactFilter}`);
    }
  };
  const toggleExpanded = (projectId: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };
  const handleAddProject = () => {
    if (!newProjectTitle.trim() || !selectedCompany) return;
    addProject({
      company_id: selectedCompany.id,
      title: newProjectTitle,
      completed: false,
      value: parseFloat(newProjectValue) || 0,
      contact_id: newProjectContactId || undefined,
      tasks: [],
      notes: []
    });
    setNewProjectTitle('');
    setNewProjectValue('');
    setNewProjectContactId('');
  };
  const handleAddTask = (projectId: string) => {
    const title = newTaskTitles[projectId];
    const value = newTaskValues[projectId];
    if (!title?.trim()) return;
    addTask(projectId, {
      title,
      completed: false,
      value: parseFloat(value) || 0
    });
    setNewTaskTitles({
      ...newTaskTitles,
      [projectId]: ''
    });
    setNewTaskValues({
      ...newTaskValues,
      [projectId]: ''
    });
  };
  const handleContactChange = (projectId: string, contactId: string) => {
    updateProject(projectId, {
      contact_id: contactId || undefined
    });
  };
  const handleOpenNoteModal = (projectId: string) => {
    setActiveProjectId(projectId);
    setNoteText('');
    setNoteDateTime('');
    setIncludeDateTime(false);
    setShowNoteModal(true);
  };
  const handleSaveNote = () => {
    if (!activeProjectId || !noteText.trim()) return;
    addNote(activeProjectId, {
      text: noteText.trim().substring(0, 50),
      date_time: includeDateTime && noteDateTime ? new Date(noteDateTime) : undefined
    });
    setShowNoteModal(false);
    setActiveProjectId(null);
    setNoteText('');
    setNoteDateTime('');
    setIncludeDateTime(false);
  };
  const handleDeleteNote = (projectId: string, noteId: string) => {
    deleteNote(projectId, noteId);
  };
  const getTotalValue = (projectsList = projects) => {
    return projectsList.reduce((sum, project) => sum + project.value, 0);
  };
  const getCompletedValue = (projectsList = projects) => {
    return projectsList.filter(p => p.completed).reduce((sum, project) => sum + project.value, 0);
  };
  const getTaskProgress = (project: (typeof projects)[0]) => {
    if (project.tasks.length === 0) return 0;
    const completed = project.tasks.filter(t => t.completed).length;
    return Math.round(completed / project.tasks.length * 100);
  };
  const getContactName = (contactId?: string) => {
    if (!contactId) return null;
    const contact = contacts.find(c => c.id === contactId);
    return contact ? `${contact.first_name} ${contact.last_name}` : null;
  };
  const filteredContact = contactFilter ? contacts.find(c => c.id === contactFilter) : null;
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-1957">
      <div className="flex items-center justify-between" data-id="element-1958">
        <div data-id="element-1959">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1960">Projects</h1>
          <p className="text-sm text-gray-600" data-id="element-1961">
            {contactFilter ? <>
                {filteredProjects.length} of {projects.length} projects •{' '}
                {formatCurrency(getTotalValue(filteredProjects))} value
              </> : <>
                {projects.length} projects • {formatCurrency(getTotalValue())}{' '}
                total value
              </>}
          </p>
        </div>
        <div className="text-right" data-id="element-1962">
          <p className="text-sm text-gray-600" data-id="element-1963">Completed</p>
          <p className="text-lg font-bold text-green-600" data-id="element-1964">
            {formatCurrency(getCompletedValue(filteredProjects))}
          </p>
        </div>
      </div>

      {/* Contact Filter Banner */}
      {contactFilter && filteredContact && <Card data-id="element-1965">
          <div className="flex items-center justify-between" data-id="element-1966">
            <div className="flex items-center gap-3" data-id="element-1967">
              <UserIcon className="w-5 h-5 text-orange-600" data-id="element-1968" />
              <div data-id="element-1969">
                <p className="text-sm font-medium text-gray-900" data-id="element-1970">
                  Showing projects for: {filteredContact.first_name}{' '}
                  {filteredContact.last_name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1971">
                  {filteredContact.company}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-1972">
              <Button variant="secondary" size="sm" onClick={goBackToContact} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1974" />} data-id="element-1973">
                Back to Contact
              </Button>
              <Button variant="ghost" size="sm" onClick={clearContactFilter} leftIcon={<XIcon className="w-4 h-4" data-id="element-1976" />} data-id="element-1975">
                Clear Filter
              </Button>
            </div>
          </div>
        </Card>}

      {/* Note Modal */}
      <Modal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} title="Add Note" data-id="element-1977">
        <div className="space-y-4" data-id="element-1978">
          <div data-id="element-1979">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1980">
              Note Text{' '}
              <span className="text-gray-500" data-id="element-1981">(max 50 characters)</span>
            </label>
            <input type="text" maxLength={50} placeholder="e.g., Call client tomorrow, Waiting on approval..." value={noteText} onChange={e => setNoteText(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1982" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-1983">
              {noteText.length}/50 characters
            </p>
          </div>

          <div className="flex items-center gap-2" data-id="element-1984">
            <Checkbox checked={includeDateTime} onChange={checked => setIncludeDateTime(checked)} data-id="element-1985" />
            <label className="text-sm font-medium text-gray-700" data-id="element-1986">
              Include date & time
            </label>
          </div>

          {includeDateTime && <div data-id="element-1987">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1988">
                Date & Time
              </label>
              <input type="datetime-local" value={noteDateTime} onChange={e => setNoteDateTime(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1989" />
            </div>}

          <div className="flex justify-end gap-2 pt-2" data-id="element-1990">
            <Button variant="ghost" onClick={() => setShowNoteModal(false)} data-id="element-1991">
              Cancel
            </Button>
            <Button onClick={handleSaveNote} disabled={!noteText.trim()} data-id="element-1992">
              Add Note
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add New Project */}
      <Card data-id="element-1993">
        <div className="space-y-3" data-id="element-1994">
          <div className="flex gap-3" data-id="element-1995">
            <input type="text" placeholder="New project title..." value={newProjectTitle} onChange={e => setNewProjectTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddProject()} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1996" />
            <div className="relative w-32" data-id="element-1997">
              <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1998" />
              <input type="number" placeholder="Value" value={newProjectValue} onChange={e => setNewProjectValue(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddProject()} className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1999" />
            </div>
          </div>
          <div className="flex gap-3" data-id="element-2000">
            <select value={newProjectContactId} onChange={e => setNewProjectContactId(e.target.value)} className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-2001">
              <option value="" data-id="element-2002">Select Customer (Optional)</option>
              {contacts.map(contact => <option key={contact.id} value={contact.id} data-id="element-2003">
                  {contact.first_name} {contact.last_name} - {contact.company}
                </option>)}
            </select>
            <Button onClick={handleAddProject} size="sm" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2005" />} data-id="element-2004">
              Add
            </Button>
          </div>
        </div>
      </Card>

      {/* Projects List */}
      <div className="space-y-3" data-id="element-2006">
        {filteredProjects.map(project => {
        const isExpanded = expandedProjects.has(project.id);
        const progress = getTaskProgress(project);
        const contactName = getContactName(project.contact_id);
        const notes = project.notes || [];
        return <Card key={project.id} padding="none" data-id="element-2007">
              {/* Project Header */}
              <div className="p-4" data-id="element-2008">
                <div className="flex items-start gap-3" data-id="element-2009">
                  <Checkbox checked={project.completed} onChange={() => toggleProjectComplete(project.id)} className="mt-0.5" data-id="element-2010" />
                  <button onClick={() => toggleExpanded(project.id)} className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-2011">
                    {isExpanded ? <ChevronDownIcon className="w-4 h-4 text-gray-600" data-id="element-2012" /> : <ChevronRightIcon className="w-4 h-4 text-gray-600" data-id="element-2013" />}
                  </button>
                  <div className="flex-1 min-w-0" data-id="element-2014">
                    <div className="flex items-center justify-between gap-3 mb-2" data-id="element-2015">
                      <div className="flex-1 min-w-0" data-id="element-2016">
                        <h3 className={`text-base font-semibold ${project.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`} data-id="element-2017">
                          {project.title}
                        </h3>
                        {contactName && <div className="flex items-center gap-1 mt-1" data-id="element-2018">
                            <UserIcon className="w-3 h-3 text-gray-400" data-id="element-2019" />
                            <span className="text-xs text-gray-500" data-id="element-2020">
                              {contactName}
                            </span>
                          </div>}
                      </div>
                      <div className="flex items-center gap-3" data-id="element-2021">
                        <span className="text-sm font-semibold text-gray-900 whitespace-nowrap" data-id="element-2022">
                          {formatCurrency(project.value)}
                        </span>
                        <button onClick={() => handleOpenNoteModal(project.id)} className="p-1 text-gray-400 hover:text-yellow-600 transition-colors" title="Add note" data-id="element-2023">
                          <StickyNoteIcon className="w-4 h-4" data-id="element-2024" />
                        </button>
                        <button onClick={() => deleteProject(project.id)} className="p-1 text-gray-400 hover:text-red-600 transition-colors" data-id="element-2025">
                          <TrashIcon className="w-4 h-4" data-id="element-2026" />
                        </button>
                      </div>
                    </div>

                    {/* Notes Display */}
                    {notes.length > 0 && <div className="flex flex-wrap gap-2 mb-2" data-id="element-2027">
                        {notes.map(note => <div key={note.id} className="group relative inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1 text-xs" data-id="element-2028">
                            <StickyNoteIcon className="w-3 h-3 text-yellow-600 flex-shrink-0" data-id="element-2029" />
                            <span className="text-yellow-900 font-medium" data-id="element-2030">
                              {note.text}
                            </span>
                            {note.date_time && <div className="flex items-center gap-1 text-yellow-700" data-id="element-2031">
                                <ClockIcon className="w-3 h-3" data-id="element-2032" />
                                <span data-id="element-2033">
                                  {new Date(note.date_time).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                                </span>
                              </div>}
                            <button onClick={() => handleDeleteNote(project.id, note.id)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-yellow-600 hover:text-red-600" data-id="element-2034">
                              <XIcon className="w-3 h-3" data-id="element-2035" />
                            </button>
                          </div>)}
                      </div>}

                    {project.tasks.length > 0 && <div className="flex items-center gap-3" data-id="element-2036">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-2037">
                          <div className="h-full bg-blue-500 transition-all duration-300" style={{
                      width: `${progress}%`
                    }} data-id="element-2038" />
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap" data-id="element-2039">
                          {project.tasks.filter(t => t.completed).length}/
                          {project.tasks.length} tasks
                        </span>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Tasks (when expanded) */}
              {isExpanded && <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3" data-id="element-2040">
                  {/* Customer Selection */}
                  <div className="pl-8" data-id="element-2041">
                    <label className="text-xs font-medium text-gray-600 mb-1 block" data-id="element-2042">
                      Customer
                    </label>
                    <select value={project.contact_id || ''} onChange={e => handleContactChange(project.id, e.target.value)} className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-2043">
                      <option value="" data-id="element-2044">No customer assigned</option>
                      {contacts.map(contact => <option key={contact.id} value={contact.id} data-id="element-2045">
                          {contact.first_name} {contact.last_name} -{' '}
                          {contact.company}
                        </option>)}
                    </select>
                  </div>

                  {/* Tasks */}
                  {project.tasks.map(task => <div key={task.id} className="flex items-center gap-3 pl-8" data-id="element-2046">
                      <Checkbox checked={task.completed} onChange={() => toggleTaskComplete(project.id, task.id)} data-id="element-2047" />
                      <span className={`flex-1 text-sm ${task.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`} data-id="element-2048">
                        {task.title}
                      </span>
                      <span className="text-sm font-medium text-gray-600" data-id="element-2049">
                        {formatCurrency(task.value)}
                      </span>
                      <button onClick={() => deleteTask(project.id, task.id)} className="p-1 text-gray-400 hover:text-red-600 transition-colors" data-id="element-2050">
                        <TrashIcon className="w-3 h-3" data-id="element-2051" />
                      </button>
                    </div>)}

                  {/* Add Task */}
                  <div className="flex gap-2 pl-8 pt-2" data-id="element-2052">
                    <input type="text" placeholder="Add task..." value={newTaskTitles[project.id] || ''} onChange={e => setNewTaskTitles({
                ...newTaskTitles,
                [project.id]: e.target.value
              })} onKeyDown={e => e.key === 'Enter' && handleAddTask(project.id)} className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-2053" />
                    <div className="relative w-24" data-id="element-2054">
                      <DollarSignIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" data-id="element-2055" />
                      <input type="number" placeholder="Value" value={newTaskValues[project.id] || ''} onChange={e => setNewTaskValues({
                  ...newTaskValues,
                  [project.id]: e.target.value
                })} onKeyDown={e => e.key === 'Enter' && handleAddTask(project.id)} className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-2056" />
                    </div>
                    <Button onClick={() => handleAddTask(project.id)} size="sm" variant="secondary" data-id="element-2057">
                      Add
                    </Button>
                  </div>
                </div>}
            </Card>;
      })}
      </div>

      {filteredProjects.length === 0 && <Card data-id="element-2058">
          <div className="text-center py-8" data-id="element-2059">
            <p className="text-gray-500 mb-4" data-id="element-2060">
              {contactFilter ? `No projects found for ${filteredContact?.first_name} ${filteredContact?.last_name}` : 'No projects yet. Add your first project above!'}
            </p>
            {contactFilter && <div className="flex items-center justify-center gap-3" data-id="element-2061">
                <Button onClick={goBackToContact} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-2063" />} data-id="element-2062">
                  Back to Contact
                </Button>
                <Button onClick={clearContactFilter} variant="secondary" data-id="element-2064">
                  View All Projects
                </Button>
              </div>}
          </div>
        </Card>}
    </div>;
}